/**
 * RunsOverlay — a docked glass side panel listing the topology's runs, active
 * first. The full-screen analogue of the right-hand run panel; click to focus.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio } from "lucide-react";
import { useRuns } from "../../../api/hooks.js";
import { useWorldStore } from "../../../stores/world.js";
import { StatusBadge } from "../../../components/common/StatusBadge.js";
import type { RunInfo } from "../../../api/client.js";
import { formatDuration } from "./format.js";
import { isActiveRun, orderRuns } from "./runs.js";

function RunRow({
  run,
  now,
  selected,
  onSelect,
}: {
  run: RunInfo;
  now: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const active = isActiveRun(run);
  const elapsed = formatDuration((run.completedAt ?? now) - run.startedAt);

  return (
    <button
      onClick={onSelect}
      className={`group w-full rounded-xl border px-3 py-2.5 text-left transition-[background-color,border-color,scale] duration-200 active:scale-[0.98] ${
        selected
          ? "border-accent/40 bg-accent/10"
          : "border-white/5 bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.05]"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 min-w-0">
          {active && (
            <span className="relative flex h-1.5 w-1.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-green opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-green" />
            </span>
          )}
          <span className="truncate font-mono text-xs text-text-secondary">
            {run.id.slice(0, 8)}
          </span>
        </span>
        <StatusBadge status={run.status} />
      </div>
      <p className="mt-1.5 line-clamp-1 text-xs text-text-muted">{run.input || "—"}</p>
      <div className="mt-1.5 flex items-center gap-3 font-mono text-[10px] tabular-nums text-text-muted">
        <span>{elapsed}</span>
        <span>${run.totalCostUsd.toFixed(4)}</span>
      </div>
    </button>
  );
}

export function RunsOverlay() {
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const selectedRunId = useWorldStore((s) => s.selectedRunId);
  const selectRun = useWorldStore((s) => s.selectRun);
  const { data: runs } = useRuns(selectedTopologyId ?? undefined);

  // Tick once a second so elapsed times advance smoothly between refetches.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ordered = orderRuns(runs ?? []);

  return (
    <motion.aside
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 24 }}
      transition={{ type: "spring", duration: 0.4, bounce: 0 }}
      className="overlay-panel pointer-events-auto flex h-full w-72 flex-col rounded-2xl"
    >
      <header className="flex items-center justify-between border-b border-white/5 px-4 py-3">
        <span className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-accent-light" />
          <h2 className="text-sm font-medium text-text-primary">Runs</h2>
        </span>
        <span className="rounded-full bg-white/5 px-2 py-0.5 font-mono text-[10px] tabular-nums text-text-muted">
          {ordered.length}
        </span>
      </header>

      <div className="flex-1 space-y-1.5 overflow-y-auto p-2.5">
        {ordered.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <Radio className="h-5 w-5 text-text-muted/50" />
            <p className="text-xs text-text-muted">No runs yet. Start one to see it here.</p>
          </div>
        ) : (
          ordered.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              now={now}
              selected={run.id === selectedRunId}
              onSelect={() => selectRun(run.id === selectedRunId ? null : run.id)}
            />
          ))
        )}
      </div>
    </motion.aside>
  );
}
